import { useState, useCallback, useEffect, useMemo } from "react";
import { useAuth } from "../../context/AuthProvider";
import { useLanguage } from "../../context/LanguageProvider";
import { BASE_URL } from '../../apiConfig';
import { toast } from 'sonner'; 
import { Link } from "react-router-dom";

const VARIANT_TYPES = [
  { value: "color", label: "Color", icon: "🎨" },
  { value: "size", label: "Size", icon: "📏" },
  { value: "material", label: "Material", icon: "🧵" },
  { value: "other", label: "Other", icon: "⚙️" },
];

const PREDEFINED_COLORS = [
  "var(--color-primary-500)", "#3b82f6", "#ef4444", "#f59e0b",
  "#10b981", "#8b5cf6", "#ec4899", "#06b6d4",
  "#84cc16", "#f97316", "#6366f1", "#14b8a6",
  "#000000", "#ffffff", "#6b7280", "#1f2937",
  "#fbbf24", "#dc2626",
];

const isHexColor = (value) => /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value);

export default function UpdateVariantForm({ variant }) {
  const { t } = useLanguage();
  const { token } = useAuth();
  

  // Initialize formData with the variant prop, or an empty structure for new variants if this form were reused for creation
  const [formData, setFormData] = useState(variant || {
    id: null, // Assuming ID is part of variant for PUT/PATCH
    type: 'color', // Default type
    name: '',
    value: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({}); // Renamed for clarity from 'errors'
  const [apiError, setApiError] = useState("");

  useEffect(() => {
    console.log("useEffect - variant prop updated:", variant);
    if (variant) {
      setFormData(variant);
      setValidationErrors({}); // Clear errors when a new variant is loaded
      setApiError("");
    }
  }, [variant]);

  // Handle input changes with improved error clearing
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    console.log(`handleInputChange fired! Input Name: ${name}, Value: ${value}`);

    setFormData(prev => {
      const newState = { ...prev, [name]: value };
      console.log("handleInputChange - attempting to set new state:", newState);

      // Logic for clearing value if type changes from color and value is a color code
      if (name === "type" && prev.type === "color" && value !== "color" && isHexColor(prev.value)) {
        console.log("Type changed from color, clearing value.");
        newState.value = "";
      }
      return newState;
    });
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined })); // Use undefined to truly remove the key
    }
    setApiError(""); // Clear general API error on user interaction
  }, [validationErrors]); // Dependency on validationErrors to correctly clear

  // Handle color selection
  const handleColorSelect = useCallback((color) => {
    setFormData(prev => ({ ...prev, value: color }));
    if (validationErrors.value) {
      setValidationErrors(prev => ({ ...prev, value: undefined }));
    }
    setApiError("");
  }, [validationErrors]);

  // Form Submission Handler
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setValidationErrors({}); // Clear previous backend validation errors
    setApiError(""); // Clear previous general API errors

    if (!token) {
      toast.error(t("variants.auth_required"));
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${BASE_URL}/api/variants/${formData.id}`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        if (response.status === 422 && result.errors) {
          setValidationErrors(result.errors);
          toast.error(result.message || t("variants.errors.highlighted_fields"));
        } else {
          const errorMessage = result.message || `${t("messages.error")}: ${response.statusText}`;
          setApiError(errorMessage);
          toast.error(errorMessage);
        }
        return; // Stop execution if there's an error
      }

      toast.success(t("variants.success_update"));
    
      setValidationErrors({}); // Ensure all error states are cleared on success
      setApiError("");

    } catch (error) {
      console.error("Network or unexpected error during variant update:", error);
      setApiError(t("variants.errors.network_error"));
      toast.error(t("variants.errors.unexpected"));
    } finally {
      setIsLoading(false);
    }
  }, [formData, token]); // Dependencies: formData (for body), token (for auth)

  const previewData = useMemo(() => {
    const selectedType = VARIANT_TYPES.find(type => type.value === formData.type);
    return {
      displayValue: formData.value || t("variants.no_value"),
      displayName: formData.name || t("variants.variant_name_default"),
      displayType: selectedType ? t(`variants.type_${selectedType.value}`).toUpperCase() : "TYPE",
      isColorTypeAndValid: formData.type === "color" && isHexColor(formData.value),
    };
  }, [formData.type, formData.value, formData.name]);

  return (
    <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
        <p className="text-center text-[10px] max-xl:text-[10px] text-red-500 max-w-2xl mx-auto mt-5">
          {t("variants.warning")}
        </p>
      <form onSubmit={handleSubmit} className="p-8">
        {/* API Error Display */}
        {apiError && (
          <div className="mb-6 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-center font-medium" role="alert">
            <p>{apiError}</p>
          </div>
        )}

        <div className="mb-8">
          <label className="block font-semibold text-gray-900 mb-4 text-sm">{t("variants.variant_type_label")} <span className="text-red-500">*</span></label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {VARIANT_TYPES.map((type) => (
              <label
                key={type.value}
                htmlFor={`variant-type-${type.value}`} // Linked to input ID
                className={`flex items-center py-2 px-3 text-xs rounded-2xl cursor-pointer transition-all duration-200 ${
                  formData.type === type.value
                    ? "bg-[var(--color-primary-50)] border-2 border-[var(--color-primary-500)] text-[var(--color-primary-500)] shadow-md"
                    : "bg-gray-50 border-2 border-gray-200 hover:border-[var(--color-primary-500)] text-gray-700 hover:bg-gray-100"
                }`}
              >
                <input
                  type="radio"
                  id={`variant-type-${type.value}`} // Unique ID for each radio button
                  name="type" // Same name for all radio buttons in the group
                  value={type.value}
                  checked={formData.type === type.value} // Controlled component: checked state tied to formData.type
                  onChange={handleInputChange} // The handler for state updates
                  className="sr-only" // Visually hide the native radio button
                  aria-label={`Select variant type ${type.label}`}
                />
                <span className="text-lg mr-3">{type.icon}</span>
                <span className="font-medium text-xs">{t(`variants.type_${type.value}`)}</span>
              </label>
            ))}
          </div>
          {validationErrors.type && <p className="mt-2 text-red-600 text-xs font-medium">{validationErrors.type[0]}</p>}
        </div>

        {/* Variant Name */}
        <div className="mb-8">
          <label htmlFor="name" className="block font-semibold text-gray-900 mb-3 text-sm">{t("variants.variant_name_label")} <span className="text-red-500">*</span></label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name || ''} // Ensure value is never null/undefined for controlled input
            onChange={handleInputChange}
            placeholder={t("variants.name_placeholder")}
            className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
              validationErrors.name
                ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
            }`}
            aria-invalid={!!validationErrors.name} // ARIA for accessibility
            aria-describedby={validationErrors.name ? "name-error" : undefined}
          />
          {validationErrors.name && <p id="name-error" className="mt-2 text-red-600 text-sm font-medium">{validationErrors.name[0]}</p>}
        </div>

        {/* Variant Value */}
        <div className="mb-8">
          <label htmlFor="value" className="block font-semibold text-gray-900 mb-3 text-sm">{t("variants.variant_value_label")} <span className="text-red-500">*</span></label>
          {formData.type === "color" ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <input
                  type="text"
                  id="value"
                  name="value"
                  value={formData.value || ''}
                  onChange={handleInputChange}
                  placeholder={t("variants.color_placeholder")}
                  className={`flex-1 py-3 px-4 text-xs border-2 outline-none rounded-2xl bg-gray-50 focus:bg-white font-mono ${
                    validationErrors.value
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                  aria-invalid={!!validationErrors.value}
                  aria-describedby={validationErrors.value ? "value-error" : undefined}
                />
                {isHexColor(formData.value) && (
                  <div
                    className="w-12 h-12 rounded-2xl border-2 border-gray-200 shadow-sm flex-shrink-0"
                    style={{ backgroundColor: formData.value }}
                    title={`${t("variants.selected_color")}: ${formData.value}`}
                  />
                )}
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-3">{t("variants.choose_predefined_colors")}</p>
                <div className="grid grid-cols-6 md:grid-cols-9 gap-3">
                  {PREDEFINED_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => handleColorSelect(color)}
                      className={`w-10 h-10 rounded-xl border-2 transition-all duration-200 hover:scale-110 flex-shrink-0 ${
                        formData.value === color
                          ? "border-[var(--color-primary-500)] ring-4 ring-[var(--color-primary-100)]"
                          : "border-gray-200"
                      }`}
                      style={{ backgroundColor: color }}
                      title={`${t("variants.select_color")}: ${color}`}
                      aria-pressed={formData.value === color}
                    />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <input
              type="text"
              id="value"
              name="value"
              value={formData.value || ''}
              onChange={handleInputChange}
              placeholder={t("variants.value_placeholder")}
              className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl bg-gray-50 focus:bg-white ${
                validationErrors.value
                  ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                  : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
              }`}
              aria-invalid={!!validationErrors.value}
              aria-describedby={validationErrors.value ? "value-error" : undefined}
            />
          )}
          {validationErrors.value && <p id="value-error" className="mt-2 text-red-600 text-xs font-medium">{validationErrors.value[0]}</p>}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200 mt-10">
          <Link to="/variants" className="block text-center flex-1 py-3 px-4 cursor-pointer text-sm border-2 border-gray-200 text-gray-700 font-semibold rounded-2xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-200">
            <button
              type="button"
              disabled={isLoading}
              className="cursor-pointer"
            >
              {t("buttons.cancel")}  
            </button>
          </Link> 
          <button
            type="submit"
            disabled={isLoading}
            className={`flex-1 py-3 px-4 cursor-pointer text-sm font-semibold rounded-2xl flex items-center justify-center gap-2 transition-all duration-200 ${
              isLoading
                ? "bg-gray-400 text-white cursor-not-allowed"
                : "bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] text-white shadow-lg hover:shadow-xl transform hover:scale-[1.01]" // Subtle scale on hover
            }`}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {t("variants.saving")}
              </>
            ) : (
              <>
                {t("variants.save_changes")}
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /> {/* Checkmark icon */}
                </svg>
              </>
            )}
          </button>
        </div>

        {(formData.name || formData.value || previewData.isColorTypeAndValid) && ( // Show preview if any relevant data exists
          <div className="mt-10 p-6 bg-gray-50 rounded-2xl border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-4">{t("variants.live_preview")}</h3>
            <div className="flex text-sm items-center gap-4">
              {previewData.isColorTypeAndValid ? (
                <div
                  className="w-10 h-10 rounded-full border-2 border-gray-200 shadow-md flex-shrink-0"
                  style={{ backgroundColor: previewData.displayValue }}
                  aria-label={`${t("variants.preview_color")}: ${previewData.displayValue}`}
                />
              ) : (
                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-500 font-bold text-sm flex-shrink-0">
                  {previewData.displayValue ? String(previewData.displayValue).slice(0, 2).toUpperCase() : "??"}
                </div>
              )}
              <div>
                <div className="font-bold text-gray-900">{previewData.displayName}</div>
                <div className="text-xs text-gray-500 mt-1">
                  <span className="font-medium">{previewData.displayType}</span>: {previewData.displayValue}
                </div>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}